<template>
  <div
    :id="id"
    :class="$b()"
  >
    <div :class="$b('heading-row')">
      <GenericStructuredText
        v-if="block.content"
        :content="block.content"
        copy-size="body-sm"
      />

      <div
        v-show="block.items.length > 3"
        :class="
          $b('controls', {
            hidden: !leftArrowVisible && !rightArrowVisible,
          })
        "
      >
        <CarouselArrow
          arrow="left"
          :is-disabled="!leftArrowVisible"
        />
        <CarouselArrow
          arrow="right"
          :is-disabled="!rightArrowVisible"
        />
      </div>
    </div>

    <Swiper
      :class="$b('items')"
      :modules="modules"
      :slides-per-view="1.8"
      :slides-per-group="1"
      :space-between="12"
      :breakpoints="{
        375: {
          slidesPerView: 2.3,
        },
        800: {
          slidesPerView: 3.8,
        },
        1000: {
          slidesPerView: 3.8,
        },
        1150: {
          slidesPerView: 4.3,
        },
        1500: {
          slidesPerView: 6.3,
        },
        1920: {
          slidesPerView: 7,
        },
      }"
      :observer="false"
      :navigation="{
        nextEl: `#${id} [data-carousel-arrow=right]`,
        prevEl: `#${id} [data-carousel-arrow=left]`,
      }"
      :scrollbar="{
        hide: true,
      }"
      @reach-end="handleReachEnd"
      @slide-change="handleSlideChange"
    >
      <SwiperSlide
        v-for="(item, slideIndex) in block.items"
        :key="item.id"
        :class="$b('slide')"
      >
        <CarouselItem
          v-if="item.type === 'carousel_item'"
          v-bind="item"
          @click="handleCardClick(slideIndex + 1)"
        />
        <ProductData
          v-else-if="item.type === 'product_card'"
          :key="item.id"
          :price-override="block.globalPriceOverride"
          :use-base-product="item.useBaseProduct"
          :base-product-sku="
            item.useBaseProduct
              ? item.baseProduct
                ? item.baseProduct.sku
                : null
              : null
          "
          :products="item.useBaseProduct ? [] : item.products"
          :variant-quantity-limit="5"
          :use-prescription-pricing="item.isPrescriptionProducts"
          :lite="true"
        >
          <template #default="{ products }">
            <LiteProductCard
              :product="products"
              :show-swatch-selector="item.showSwatchSelector"
              :class="$b('product-card')"
              :compact-view="true"
              @click-giftcard="$emit('click-giftcard', $event)"
              @click="handleCardClick(slideIndex + 1)"
            />
          </template>
        </ProductData>
      </SwiperSlide>
    </Swiper>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Scrollbar } from 'swiper/modules'

export default {
  name: 'CarouselRow',

  components: {
    Swiper,
    SwiperSlide,
  },

  props: {
    block: { type: Object, required: true },
    rowPosition: { type: Number, default: 0 },
  },

  emits: ['click-giftcard'],

  setup() {
    return {
      modules: [Navigation, Scrollbar],
    }
  },

  data: () => ({
    leftArrowVisible: false,
    rightArrowVisible: true,
  }),

  computed: {
    id() {
      return 'carousel-row--' + this.block.id
    },
  },

  methods: {
    handleSlideChange(swiper) {
      this.leftArrowVisible = !swiper.isBeginning
      this.rightArrowVisible = !swiper.isEnd
    },

    async handleCardClick(elementId) {
      await this.$track.global.linkClicked({
        interactionElement: 'Card - ' + elementId,
        position: this.rowPosition,
        interactionSection: this.block.trackingId,
        interactionCategory: 'Carousel',
        position: this.rowPosition + 1,
      }, 'Clicked Product Carousel Item')
    },

    handleReachEnd() {
      this.rightArrowVisible = false
    },
  },
}
</script>

<style lang="postcss">
.CarouselRow {
  @apply w-full;
}

.CarouselRow__heading-row {
  @apply flex justify-between items-end
    mb-sm global-wrapper-large gap-x-2;
}

.CarouselRow__controls {
  @apply flex gap-x-3;
}

.CarouselRow__controls--hidden {
  @apply hidden;
}

.CarouselRow__arrow {
  @apply opacity-100 transition-opacity;

  &--hidden {
    @apply opacity-0 pointer-events-none;
  }
}

.CarouselRow__scrollbar-container {
  @apply mx-auto;
  max-width: theme('screens.full');
}

.CarouselRow__items,
.swiper-container.Carousel__items {
  @apply w-screen carousel-padding-large;

  .swiper-wrapper {
    @apply flex
      flex-row w-full;
  }

  .swiper-scrollbar {
    @apply mt-xxs mx-auto
      relative left-0;
    border-radius: 18px;
    -ms-touch-action: none;
    background-color: transparent;
    height: 5px;
    max-width: theme('screens.full');
    width: 100%;
  }

  .swiper-scrollbar-drag {
    @apply bg-grey transition-colors;

    height: 100%;
    width: 100%;
    position: relative;
    border-radius: 10px;
    left: 0;
    top: 0;

    &:hover {
      @apply bg-off-black bg-opacity-30;
    }
  }

  .swiper-scrollbar-cursor-drag {
    cursor: move;
  }

  .swiper-scrollbar-lock {
    display: none;
  }
}

.CarouselRow__items {
  @apply overflow-visible overflow-x-hidden;

  .swiper-wrapper {
    @apply overflow-visible;
  }
}

/*

  Default styling to avoid full screen width slide items on page load
  before Swiper has initialised

*/

.swiper-container.CarouselRow__items {
  @apply flex;
}

.swiper-slide.CarouselRow__slide {
  @apply shrink-0 mr-4 h-auto;

  &:last-child {
    @apply mr-0;
  }

  @media (min-width: 375px) {
    width: 40%;
  }

  @media (min-width: 800px) {
    width: 25%;
  }

  @media (min-width: 1150px) {
    width: 22%;
  }

  @media (min-width: 1500px) {
    width: 15%;
  }

  @media (min-width: 1920px) {
    width: 14%;
  }
}
</style>
